/**
 * Translation layer.
 *
 * Turkish is the default and the source of truth: it is written first and the
 * English catalogue is the translation, not the other way round. That ordering
 * matters because it stops English phrasing leaking into the Turkish interface
 * through literal translation.
 *
 * The catalogue is a flat record rather than a nested object so that a missing
 * key is a TypeScript error at the call site instead of an `undefined` rendered
 * into the page.
 */

import { tr } from './tr'
import { en } from './en'

export type Locale = 'tr' | 'en'

/** Every translatable string key. Derived from the Turkish catalogue, which is
 *  authoritative — adding a key to `en` that `tr` lacks will not compile. */
export type MessageKey = keyof typeof tr

const catalogues: Record<Locale, Record<MessageKey, string>> = { tr, en }

const STORAGE_KEY = 'kimene.locale'

/** Reads the operator's stored preference, defaulting to Turkish. */
export function loadLocale(): Locale {
  if (typeof localStorage === 'undefined') return 'tr'
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'en' || stored === 'tr' ? stored : 'tr'
}

export function saveLocale(locale: Locale): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, locale)
}

/**
 * Translates a key, substituting `{name}` placeholders.
 *
 * A key with no entry in the active catalogue falls back to Turkish rather than
 * to the raw key, so a partially translated English catalogue degrades into a
 * readable mixed interface instead of showing `dashboard.header.title` to a user.
 */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const template = catalogues[locale][key] ?? tr[key] ?? String(key)
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  )
}

/** Narrows a runtime string to a known message key.
 *
 * Some copy is chosen by a value the server sends — a risk `kind`, for example.
 * The catalogue cannot type those at compile time, and a plain cast would let a
 * missing entry render as the key itself. This lets a caller ask first and
 * choose its own fallback, which for server-composed text is the server's own
 * sentence rather than `risk.some_new_detector`.
 */
export function hasMessage(key: string): key is MessageKey {
  return key in tr
}

/** IANA zone used for rendering. Storage is always UTC; only display converts. */
export const DISPLAY_TIMEZONE = 'Europe/Istanbul'

/** Formats an ISO-8601 UTC timestamp in the operator's display zone. */
export function formatDateTime(iso: string | undefined | null, locale: Locale): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return new Intl.DateTimeFormat(locale === 'tr' ? 'tr-TR' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'medium',
    timeZone: DISPLAY_TIMEZONE,
  }).format(d)
}

/**
 * Formats an elapsed duration relative to now, e.g. "3 dk önce".
 *
 * Uses Intl.RelativeTimeFormat so the Turkish output is grammatical rather than
 * a hand-assembled string, which is where locale-aware formatting usually goes
 * wrong.
 */
export function formatRelative(iso: string | undefined | null, locale: Locale): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const seconds = Math.round((then - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale === 'tr' ? 'tr-TR' : 'en-GB', {
    numeric: 'auto',
  })

  const divisions: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ]

  let value = seconds
  for (const [amount, unit] of divisions) {
    if (Math.abs(value) < amount) return rtf.format(Math.round(value), unit)
    value /= amount
  }
  return rtf.format(Math.round(value), 'year')
}

/** Formats a byte count with binary units. */
export function formatBytes(bytes: number | undefined | null): string {
  if (bytes == null || Number.isNaN(bytes)) return '—'
  if (bytes === 0) return '0 B'
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB']
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)), units.length - 1)
  const scaled = bytes / 1024 ** i
  return `${scaled.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

/** Unit letters per locale. Turkish is the interface's source of truth and its
 *  durations were reading `43h 9m` — English abbreviations in a Turkish console,
 *  beside a fleet screen that has always written `sa` and `dk`. */
const DURATION_UNITS: Record<Locale, { ms: string; s: string; m: string; h: string; d: string }> =
  {
    tr: { ms: 'ms', s: 'sn', m: 'dk', h: 'sa', d: 'g' },
    en: { ms: 'ms', s: 's', m: 'm', h: 'h', d: 'd' },
  }

/**
 * A duration, at the coarsest pair of units that still says something.
 *
 * The day step is not cosmetic. Without it this function counted hours forever,
 * and a session that had been running since the week before rendered as
 * `448sa 27dk` — a number an operator has to divide by 24 in their head before
 * it means anything, printed in the one place on the card where they are
 * looking for "is this stale". `18g 16sa` is the same measurement, read.
 *
 * Two units, never three: the third is always smaller than the error in what is
 * being measured.
 */
export function formatDuration(ms: number | undefined | null, locale: Locale = 'tr'): string {
  if (ms == null || Number.isNaN(ms)) return '—'
  const u = DURATION_UNITS[locale] ?? DURATION_UNITS.tr
  if (ms < 1000) return `${Math.round(ms)} ${u.ms}`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)} ${u.s}`
  const m = Math.floor(s / 60)
  const rem = Math.round(s % 60)
  if (m < 60) return `${m}${u.m} ${rem}${u.s}`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}${u.h} ${m % 60}${u.m}`
  return `${Math.floor(h / 24)}${u.d} ${h % 24}${u.h}`
}

/**
 * Formats a 0..1 fraction as a percentage, or the unknown marker when the value
 * is absent.
 *
 * The null case is the point: Kimene never renders unknown capacity as a number,
 * because a dashboard showing "100%" for something it has never measured is
 * worse than one showing nothing.
 */
export function formatPercent(fraction: number | undefined | null): string {
  if (fraction == null || Number.isNaN(fraction)) return '—'
  return `${Math.round(fraction * 100)}%`
}
